import { useEffect, useState } from 'react'
import Icon from './Icons'
import { editorPathLengthKm } from '../utils/structures'
import { troncColor, troncFocusType, focusedTroncons, troncMergeBlocker } from '../utils/troncons'
import '../styles/InfraToolPanel.css'

// The infrastructure tool panel: two mouse modes and every action they allow.
//
// The rule this panel exists to enforce: nothing is only reachable by a mouse
// gesture you have to guess. The map is a shortcut for what is written here —
// selecting, reworking, resetting and merging all have a labelled button, and
// the tronçon list means a route can be picked without hunting for it on the
// map at all.

const km = (v) => Number(v || 0).toLocaleString('fr-FR', { maximumFractionDigits: 1 })

// Destructive actions ask twice, in place: the second click of the same button
// confirms. A modal for "réinitialiser" would be heavier than the action.
function ConfirmButton({ className, icon, label, confirmLabel, title, disabled, onConfirm }) {
  const [armed, setArmed] = useState(false)
  useEffect(() => {
    if (!armed) return undefined
    const t = setTimeout(() => setArmed(false), 4000)
    return () => clearTimeout(t)
  }, [armed])
  useEffect(() => { if (disabled) setArmed(false) }, [disabled])

  return (
    <button
      type="button"
      className={`${className}${armed ? ' armed' : ''}`}
      title={title}
      disabled={disabled}
      onClick={() => { if (armed) { setArmed(false); onConfirm() } else setArmed(true) }}
    >
      <Icon name={armed ? 'warning' : icon} size={13} /> {armed ? confirmLabel : label}
    </button>
  )
}

export default function InfraToolPanel({
  infraMode, onInfraModeChange,
  infraTypes, statInfraTypes, infrastructureSelection, onInfrastructureSelectionChange,
  infrastructureData, selectedTroncIds, onTroncSelect, onTroncClearSelection,
  onTroncRework, onTroncReset, onTroncMerge,
  infrastructurePath, editingInfra,
  onFinishInfrastructure, onUndoInfraPoint, onRestartInfrastructure, onCancelInfrastructure,
}) {
  const all = infrastructureData || []
  const ids = selectedTroncIds || []
  const focusType = troncFocusType(ids, all)
  // Same list, same order as the map's colour ramp — the swatch here is the
  // colour out there, which is the only reason the list is useful.
  const painted = focusedTroncons(all, focusType)
  const colorOf = (id) => {
    const index = painted.findIndex((i) => i.id === id)
    return index === -1 ? 'transparent' : troncColor(id, index)
  }
  const selected = ids.map((id) => all.find((i) => i.id === id)).filter(Boolean)
  const selectedKm = selected.reduce((sum, t) => sum + Number(t.length_km || 0), 0)
  const mergeBlocker = troncMergeBlocker(selected)

  const typeOptions = (infraTypes && infraTypes.length > 0
    ? [...infraTypes].sort((a, b) => a.type.localeCompare(b.type))
    : [...new Set(statInfraTypes || [])].sort().map((t) => ({ type: t })))

  const drawing = infraMode === 'draw'
  const hasPath = infrastructurePath && infrastructurePath.length > 0
  // Loading another route while one is being drawn would drop the unsaved work
  // without a word, so the two actions that do that are held back until the
  // editor is empty.
  const busyDrawing = hasPath ? 'Terminez ou annulez le tracé en cours d\'abord.' : null

  return (
    <>
      <div className="selection-mode infra-mode-switch">
        <div className="mode-buttons" role="group" aria-label="Mode de la souris">
          <button
            className={!drawing ? 'active' : ''}
            aria-pressed={!drawing}
            onClick={() => onInfraModeChange('select')}
            title="La souris navigue sur la carte. Cliquez un tracé pour le sélectionner. (M)"
          >
            <Icon name="search" size={13} /> Sélection
          </button>
          <button
            className={drawing ? 'active' : ''}
            aria-pressed={drawing}
            onClick={() => onInfraModeChange('draw')}
            title="Le clic gauche pose des points au lieu de déplacer la carte. (M)"
          >
            <Icon name="edit" size={13} /> Construire
          </button>
        </div>
      </div>

      <div className="plan-mode-help">
        {drawing ? (
          <p>
            <Icon name="info" size={13} /> <strong>Cliquez</strong> pour poser un point,
            <strong> maintenez</strong> pour dessiner à main levée. <strong>Glissez</strong> un
            point pour le déplacer, <strong>cliquez-le</strong> pour ses actions (relier, brancher,
            supprimer). Déplacez la carte avec les <strong>flèches</strong> du clavier ou en
            glissant hors de votre territoire.
          </p>
        ) : (
          <p>
            <Icon name="info" size={13} /> La souris navigue normalement.
            <strong> Cliquez un tracé</strong> — sur la carte ou dans la liste — pour le
            sélectionner ; chaque tronçon a sa propre couleur.
          </p>
        )}
      </div>

      {!drawing && (
        <div className="tronc-panel">
          <div className="tronc-panel-head">
            <span>
              {all.length === 0
                ? 'Aucune infrastructure construite'
                : `${painted.length} tronçon${painted.length > 1 ? 's' : ''}${focusType ? ` · ${focusType}` : ''}`}
            </span>
            {ids.length > 0 && (
              <button
                type="button"
                className="tronc-clear"
                onClick={onTroncClearSelection}
                title="Vider la sélection (Échap)"
              >
                <Icon name="close" size={12} /> {ids.length} sélectionné{ids.length > 1 ? 's' : ''}
              </button>
            )}
          </div>

          {all.length > 0 && (
            <ul className="tronc-list">
              {painted.map((t) => {
                const isSelected = ids.includes(t.id)
                return (
                  <li key={t.id}>
                    <button
                      type="button"
                      className={`tronc-row${isSelected ? ' selected' : ''}`}
                      aria-pressed={isSelected}
                      onClick={() => onTroncSelect(t.id)}
                      title={`${t.type} #${t.id} — ${km(t.length_km)} km${t.region_name ? ` · ${t.region_name}` : ''}`}
                    >
                      <span className="tronc-swatch" style={{ background: colorOf(t.id) }} />
                      <span className="tronc-row-id">#{t.id}</span>
                      <span className="tronc-row-type">{t.type}</span>
                      <span className="tronc-row-km">{km(t.length_km)} km</span>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}

          {ids.length > 0 && (
            <div className="tronc-actions">
              <div className="tronc-actions-summary">
                {selected.length} tronçon{selected.length > 1 ? 's' : ''} — {km(selectedKm)} km
              </div>
              <button
                type="button"
                className="btn-finish-infra"
                disabled={selected.length !== 1 || !!busyDrawing}
                title={busyDrawing || (selected.length === 1
                  ? 'Charger le tracé pour le modifier point par point (budget km conservé)'
                  : 'Sélectionnez un seul tronçon pour le modifier')}
                onClick={() => onTroncRework(ids[0])}
              >
                <Icon name="edit" size={13} /> Retravailler le tracé
              </button>
              <ConfirmButton
                className="btn-undo-infra"
                icon="undo"
                label="Réinitialiser le tracé"
                confirmLabel="Confirmer la réinitialisation"
                disabled={selected.length !== 1 || !!busyDrawing}
                title={busyDrawing || (selected.length === 1
                  ? `Efface le tracé de #${ids[0]} et garde ses ${km(selected[0]?.length_km)} km payés : reposez-les où vous voulez, sans repayer.`
                  : 'Sélectionnez un seul tronçon pour le réinitialiser')}
                onConfirm={() => onTroncReset(ids[0])}
              />
              <ConfirmButton
                className="btn-cancel-infra"
                icon="merge"
                label={`Fusionner ${selected.length > 1 ? `${selected.length} tronçons` : ''}`.trim()}
                confirmLabel="Confirmer la fusion"
                disabled={!!mergeBlocker || !!busyDrawing}
                title={busyDrawing || mergeBlocker || `#${ids[0]} absorbe ${selected.length - 1} tracé(s) et leurs km. Irréversible.`}
                onConfirm={() => onTroncMerge(ids)}
              />
            </div>
          )}
        </div>
      )}

      {drawing && !editingInfra && (
        <div className="filter-group">
          <label htmlFor="infra-type-select">Type d&apos;infrastructure</label>
          <select
            id="infra-type-select"
            value={infrastructureSelection.type}
            onChange={(e) => onInfrastructureSelectionChange('type', e.target.value)}
          >
            <option value="">Choisir un type…</option>
            {typeOptions.map((t) => (
              <option key={t.type} value={t.type}>
                {t.type}{t.cost_per_km ? ` — ${Number(t.cost_per_km).toLocaleString('fr-FR')} crédits/km` : ''}
              </option>
            ))}
          </select>
        </div>
      )}

      {drawing && !editingInfra && !infrastructureSelection.type && (
        <p className="own-data-hint">Choisissez un type avant de tracer.</p>
      )}

      {drawing && editingInfra && !hasPath && (
        <div className="plan-mode-help">
          <p>
            <Icon name="info" size={13} /> Tracé #{editingInfra.id} effacé — redessinez librement,
            budget {km(editingInfra.owned_km)} km conservé. Rien n&apos;est enregistré tant que vous
            n&apos;avez pas cliqué « Enregistrer ».
          </p>
        </div>
      )}

      {drawing && hasPath && (() => {
        const lengthKm = editorPathLengthKm(infrastructurePath)
        const extensionKm = editingInfra ? Math.max(0, lengthKm - editingInfra.owned_km) : 0
        const typeForCost = editingInfra ? editingInfra.type : infrastructureSelection.type
        const costPerKm = infraTypes?.find((t) => t.type === typeForCost)?.cost_per_km || 0
        const estCost = editingInfra
          ? Math.round(extensionKm * costPerKm)
          : Math.round(lengthKm * costPerKm)
        const segCount = new Set(infrastructurePath.map((p) => p.seg || 0)).size
        return (
          <div className="infra-path-info">
            {editingInfra && (
              <span className="infra-edit-banner">
                <Icon name="edit" size={12} /> {editingInfra.type} #{editingInfra.id} — budget {km(editingInfra.owned_km)} km
              </span>
            )}
            <span>
              {infrastructurePath.length} point(s){segCount > 1 ? ` · ${segCount} branches` : ''} — {km(lengthKm)} km
              {editingInfra && ` / ${km(editingInfra.owned_km)} km`}
            </span>
            {editingInfra && extensionKm > editingInfra.owned_km * 0.005 && (
              <span className="infra-cost-preview">
                <Icon name="warning" size={12} /> Extension +{km(extensionKm)} km ≈ {estCost.toLocaleString('fr-FR')} crédits
              </span>
            )}
            {!editingInfra && costPerKm > 0 && lengthKm > 0 && (
              <span className="infra-cost-preview">≈ {estCost.toLocaleString('fr-FR')} crédits</span>
            )}
            <div className="infra-path-actions">
              <button
                className="btn-finish-infra"
                onClick={onFinishInfrastructure}
                disabled={infrastructurePath.length < 2}
                title={infrastructurePath.length < 2 ? 'Un tracé demande au moins deux points' : undefined}
              >
                <Icon name={editingInfra ? 'save' : 'check'} size={13} /> {editingInfra ? 'Enregistrer' : 'Terminer'}
              </button>
              <button className="btn-undo-infra" onClick={onUndoInfraPoint} title="Retirer le dernier point (Ctrl+Z)">
                <Icon name="undo" size={13} /> Dernier point
              </button>
              {editingInfra && (
                <button className="btn-undo-infra" onClick={onRestartInfrastructure} title="Effacer tout le tracé et redessiner (budget conservé)">
                  <Icon name="trash" size={13} /> Redessiner
                </button>
              )}
              <button className="btn-cancel-infra" onClick={onCancelInfrastructure} title="Abandonner ce tracé (Échap)">
                <Icon name="close" size={13} /> Annuler
              </button>
            </div>
          </div>
        )
      })()}
    </>
  )
}
